import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import { Settings as SettingsIcon, Moon, Bell, Server, Trash2, Loader2, Check, AlertTriangle, BookOpen, Plus, X, Brain } from 'lucide-react';

export default function Settings({ navigate }) {
  const { user, signOut } = useAuth();
  const [tab, setTab] = useState('general');
  const [notifications, setNotifications] = useState(() => {
    const saved = localStorage.getItem('sage_notifications');
    return saved ? JSON.parse(saved) : { messages: true, posts: true };
  });
  const [testingLLM, setTestingLLM] = useState(false);
  const [llmStatus, setLlmStatus] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Knowledge Base state
  const [skills, setSkills] = useState([]);
  const [loadingSkills, setLoadingSkills] = useState(false);
  const [showAddSkill, setShowAddSkill] = useState(false);
  const [newSkill, setNewSkill] = useState({ title: '', content: '' });
  const [editingSkill, setEditingSkill] = useState(null);
  
  // Password change
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    if (tab === 'knowledge') {
      fetchSkills();
    }
  }, [tab]);

  const fetchSkills = async () => {
    setLoadingSkills(true);
    try {
      const { data, error } = await supabase
        .from('knowledge_base')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      
      if (!error && data) {
        setSkills(data);
      } else {
        // Fallback to localStorage
        const localSkills = JSON.parse(localStorage.getItem(`sage_skills_${user.id}`) || '[]');
        setSkills(localSkills);
      }
    } catch (error) {
      const localSkills = JSON.parse(localStorage.getItem(`sage_skills_${user.id}`) || '[]');
      setSkills(localSkills);
    } finally {
      setLoadingSkills(false);
    }
  };

  const saveSkill = async () => {
    if (!newSkill.title.trim() || !newSkill.content.trim()) return;

    const skill = {
      id: editingSkill?.id || Date.now().toString(),
      user_id: user.id,
      title: newSkill.title.trim(),
      content: newSkill.content.trim(),
      created_at: editingSkill?.created_at || new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    try {
      if (editingSkill) {
        await supabase.from('knowledge_base').update(skill).eq('id', skill.id);
        setSkills(skills.map(s => s.id === skill.id ? skill : s));
      } else {
        await supabase.from('knowledge_base').insert(skill);
        setSkills([skill, ...skills]);
      }
    } catch (error) {
      // Fallback to localStorage
      if (editingSkill) {
        const updatedSkills = skills.map(s => s.id === skill.id ? skill : s);
        localStorage.setItem(`sage_skills_${user.id}`, JSON.stringify(updatedSkills));
        setSkills(updatedSkills);
      } else {
        const updatedSkills = [skill, ...skills];
        localStorage.setItem(`sage_skills_${user.id}`, JSON.stringify(updatedSkills));
        setSkills(updatedSkills);
      }
    }

    setNewSkill({ title: '', content: '' });
    setShowAddSkill(false);
    setEditingSkill(null);
  };

  const deleteSkill = async (skillId) => {
    try {
      await supabase.from('knowledge_base').delete().eq('id', skillId);
    } catch (error) {
      // Continue anyway
    }
    
    const updatedSkills = skills.filter(s => s.id !== skillId);
    localStorage.setItem(`sage_skills_${user.id}`, JSON.stringify(updatedSkills));
    setSkills(updatedSkills);
  };

  const handleNotificationChange = (key) => {
    const updated = { ...notifications, [key]: !notifications[key] };
    setNotifications(updated);
    localStorage.setItem('sage_notifications', JSON.stringify(updated));
  };

  const testLLMConnection = async () => {
    setTestingLLM(true);
    setLlmStatus(null);
    const startTime = Date.now();

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: [{ role: 'user', content: 'ping' }],
          userEmail: user?.email
        })
      });

      const latency = Date.now() - startTime;
      
      if (response.ok) {
        setLlmStatus({ success: true, latency });
      } else {
        setLlmStatus({ success: false, error: 'Connection failed' });
      }
    } catch (error) {
      setLlmStatus({ success: false, error: error.message });
    } finally {
      setTestingLLM(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess(false);

    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      return;
    }

    setChangingPassword(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      setPasswordSuccess(true);
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => setShowPasswordForm(false), 2000);
    } catch (error) {
      setPasswordError(error.message);
    } finally {
      setChangingPassword(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      await supabase.from('users').delete().eq('id', user.id);
      await signOut();
    } catch (error) {
      console.error('Error deleting account:', error);
    } finally {
      setDeleting(false);
    }
  };

  const tabs = [
    { id: 'general', label: 'General', icon: SettingsIcon },
    { id: 'knowledge', label: 'Knowledge', icon: Brain },
    { id: 'system', label: 'System', icon: Server }
  ];

  return (
    <div className="min-h-screen bg-[#08080c] pb-20" data-testid="settings-screen">
      {/* Header */}
      <div className="p-4 border-b border-[#1a1a24]">
        <div className="flex items-center gap-3">
          <SettingsIcon className="w-4 h-4 text-[#00d4ff]" />
          <h1 className="font-mono text-xs text-[#00d4ff] uppercase tracking-[0.2em]">Settings</h1>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-[#1a1a24]">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex-1 py-3 flex items-center justify-center gap-2 font-mono text-[10px] uppercase tracking-wider transition-all ${
              tab === t.id
                ? 'text-[#00d4ff] border-b border-[#00d4ff] bg-[#00d4ff]/5'
                : 'text-[#4a4a60] hover:text-[#7a7a9a]'
            }`}
            data-testid={`settings-tab-${t.id}`}
          >
            <t.icon className="w-3.5 h-3.5" />
            {t.label}
          </button>
        ))}
      </div>

      <div className="p-4 space-y-4">
        {/* General Tab */}
        {tab === 'general' && (
          <>
            {/* Theme */}
            <div className="bg-[#0d0d12] border border-[#1a1a24] p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Moon className="w-4 h-4 text-[#4a4a60]" />
                  <div>
                    <p className="text-xs text-[#e8e8f0]">Dark Theme</p>
                    <p className="text-[10px] text-[#4a4a60]">Always enabled</p>
                  </div>
                </div>
                <div className="border border-[#00d4ff]/30 bg-[#00d4ff]/5 text-[#00d4ff] px-2 py-1 text-[10px] font-mono">ON</div>
              </div>
            </div>

            {/* Notifications */}
            <div className="bg-[#0d0d12] border border-[#1a1a24] p-4" data-testid="notification-settings">
              <div className="flex items-center gap-3 mb-4">
                <Bell className="w-4 h-4 text-[#4a4a60]" />
                <p className="text-xs text-[#e8e8f0]">Notifications</p>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-[#7a7a9a]">New messages</span>
                  <button
                    onClick={() => handleNotificationChange('messages')}
                    className={`w-10 h-6 rounded-full transition-colors ${
                      notifications.messages ? 'bg-[#00d4ff]' : 'bg-[#1a1a24]'
                    }`}
                    data-testid="toggle-messages-notif"
                  >
                    <div className={`w-4 h-4 rounded-full bg-white transition-transform ${
                      notifications.messages ? 'translate-x-5' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-[#7a7a9a]">New posts</span>
                  <button
                    onClick={() => handleNotificationChange('posts')}
                    className={`w-10 h-6 rounded-full transition-colors ${
                      notifications.posts ? 'bg-[#00d4ff]' : 'bg-[#1a1a24]'
                    }`}
                    data-testid="toggle-posts-notif"
                  >
                    <div className={`w-4 h-4 rounded-full bg-white transition-transform ${
                      notifications.posts ? 'translate-x-5' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
              </div>
            </div>

            {/* Change Password */}
            <div className="bg-[#0d0d12] border border-[#1a1a24] p-4">
              <button
                onClick={() => setShowPasswordForm(!showPasswordForm)}
                className="w-full text-left"
                data-testid="change-password-toggle"
              >
                <p className="text-xs text-[#e8e8f0]">Change Password</p>
                <p className="text-[10px] text-[#4a4a60]">Update your account password</p>
              </button>
              
              {showPasswordForm && (
                <form onSubmit={handlePasswordChange} className="mt-4 space-y-3" data-testid="password-form">
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="New password"
                    className="w-full bg-[#08080c] border border-[#1a1a24] text-[#e8e8f0] px-3 py-2 text-xs font-mono focus:border-[#00d4ff]/50 outline-none"
                    data-testid="new-password-input"
                  />
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm password"
                    className="w-full bg-[#08080c] border border-[#1a1a24] text-[#e8e8f0] px-3 py-2 text-xs font-mono focus:border-[#00d4ff]/50 outline-none"
                    data-testid="confirm-password-input"
                  />
                  {passwordError && <p className="text-[#ff3366] text-[10px] font-mono">{passwordError}</p>}
                  {passwordSuccess && <p className="text-[#00ff88] text-[10px] font-mono">Password updated!</p>}
                  <button
                    type="submit"
                    disabled={changingPassword}
                    className="w-full border border-[#00d4ff] text-[#00d4ff] font-mono text-[10px] uppercase py-2 hover:bg-[#00d4ff]/10 disabled:opacity-50"
                    data-testid="update-password-btn"
                  >
                    {changingPassword ? 'Updating...' : 'Update Password'}
                  </button>
                </form>
              )}
            </div>

            {/* Delete Account */}
            <div className="bg-[#0d0d12] border border-[#ff3366]/20 p-4">
              <div className="flex items-center gap-3 mb-2">
                <Trash2 className="w-4 h-4 text-[#ff3366]" />
                <p className="text-xs text-[#ff3366]">Delete Account</p>
              </div>
              <p className="text-[10px] text-[#4a4a60] mb-4">This action cannot be undone.</p>
              
              {!showDeleteConfirm ? (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="w-full border border-[#ff3366]/30 text-[#ff3366] font-mono text-[10px] uppercase py-2 hover:bg-[#ff3366]/10"
                  data-testid="delete-account-btn"
                >
                  Delete My Account
                </button>
              ) : (
                <div className="space-y-2" data-testid="delete-confirm">
                  <p className="text-[10px] text-[#ff3366]">Are you sure?</p>
                  <div className="flex gap-2">
                    <button
                      onClick={handleDeleteAccount}
                      disabled={deleting}
                      className="flex-1 bg-[#ff3366] text-white font-mono text-[10px] uppercase py-2 hover:bg-[#ff3366]/80 disabled:opacity-50"
                      data-testid="confirm-delete-btn"
                    >
                      {deleting ? 'Deleting...' : 'Yes, Delete'}
                    </button>
                    <button
                      onClick={() => setShowDeleteConfirm(false)}
                      className="flex-1 border border-[#1a1a24] text-[#4a4a60] font-mono text-[10px] uppercase py-2"
                      data-testid="cancel-delete-btn"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* Knowledge Base Tab */}
        {tab === 'knowledge' && (
          <div data-testid="knowledge-base-tab">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-xs text-[#e8e8f0]">Knowledge Base</p>
                <p className="text-[10px] text-[#4a4a60]">Teach Sage skills and information</p>
              </div>
              <button
                onClick={() => { setShowAddSkill(true); setEditingSkill(null); setNewSkill({ title: '', content: '' }); }}
                className="border border-[#00d4ff] text-[#00d4ff] p-2 hover:bg-[#00d4ff]/10"
                data-testid="add-knowledge-btn"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>

            {/* Add/Edit Skill Form */}
            {showAddSkill && (
              <div className="bg-[#0d0d12] border border-[#00d4ff]/20 p-4 mb-4" data-testid="skill-form">
                <div className="flex items-center justify-between mb-3">
                  <span className="font-mono text-[10px] text-[#00d4ff] uppercase">
                    {editingSkill ? 'Edit Skill' : 'Add Skill'}
                  </span>
                  <button onClick={() => { setShowAddSkill(false); setEditingSkill(null); }} className="p-1">
                    <X className="w-3.5 h-3.5 text-[#4a4a60]" />
                  </button>
                </div>
                
                <input
                  type="text"
                  value={newSkill.title}
                  onChange={(e) => setNewSkill({ ...newSkill, title: e.target.value })}
                  placeholder="Skill name"
                  className="w-full bg-[#08080c] border border-[#1a1a24] text-[#e8e8f0] px-3 py-2 text-xs font-mono mb-2 focus:border-[#00d4ff]/50 outline-none"
                  data-testid="knowledge-title-input"
                />
                
                <textarea
                  value={newSkill.content}
                  onChange={(e) => setNewSkill({ ...newSkill, content: e.target.value })}
                  placeholder="Describe the skill or knowledge..."
                  className="w-full bg-[#08080c] border border-[#1a1a24] text-[#e8e8f0] px-3 py-2 text-xs font-mono mb-3 focus:border-[#00d4ff]/50 outline-none resize-none"
                  rows={4}
                  data-testid="knowledge-content-input"
                />
                
                <button
                  onClick={saveSkill}
                  disabled={!newSkill.title.trim() || !newSkill.content.trim()}
                  className="w-full border border-[#00d4ff] text-[#00d4ff] font-mono text-[10px] uppercase py-2 hover:bg-[#00d4ff]/10 disabled:opacity-30"
                  data-testid="save-knowledge-btn"
                >
                  {editingSkill ? 'Update Skill' : 'Save Skill'}
                </button>
              </div>
            )}

            {/* Skills List */}
            {loadingSkills ? (
              <div className="text-center py-8">
                <Loader2 className="w-5 h-5 text-[#00d4ff] animate-spin mx-auto" />
              </div>
            ) : skills.length === 0 ? (
              <div className="text-center py-8 bg-[#0d0d12] border border-[#1a1a24]">
                <BookOpen className="w-8 h-8 text-[#1a1a24] mx-auto mb-3" />
                <p className="text-[#4a4a60] text-xs font-mono">No skills yet</p>
                <p className="text-[#4a4a60] text-[10px] mt-1">Add knowledge to make Sage smarter</p>
              </div>
            ) : (
              <div className="space-y-2" data-testid="skills-list">
                {skills.map((skill) => (
                  <div
                    key={skill.id}
                    className="bg-[#0d0d12] border border-[#1a1a24] p-4 hover:border-[#00d4ff]/20 transition-colors"
                    data-testid={`skill-${skill.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-[#e8e8f0] font-medium">{skill.title}</p>
                        <p className="text-[10px] text-[#7a7a9a] mt-1 line-clamp-2">{skill.content}</p>
                        <p className="text-[9px] text-[#4a4a60] mt-2 font-mono">
                          {new Date(skill.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex gap-1">
                        <button
                          onClick={() => {
                            setEditingSkill(skill);
                            setNewSkill({ title: skill.title, content: skill.content });
                            setShowAddSkill(true);
                          }}
                          className="p-2 hover:bg-[#00d4ff]/10 text-[#4a4a60] hover:text-[#00d4ff]"
                          data-testid={`edit-skill-${skill.id}`}
                        >
                          <BookOpen className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => deleteSkill(skill.id)}
                          className="p-2 hover:bg-[#ff3366]/10 text-[#4a4a60] hover:text-[#ff3366]"
                          data-testid={`delete-skill-${skill.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-4 p-3 bg-[#0d0d12] border border-[#1a1a24]">
              <p className="text-[10px] text-[#4a4a60] font-mono">
                Tip: In Chat, type "teach Sage that..." to add knowledge directly
              </p>
            </div>
          </div>
        )}

        {/* System Tab */}
        {tab === 'system' && (
          <>
            {/* LLM Configuration */}
            <div className="bg-[#0d0d12] border border-[#1a1a24] p-4" data-testid="llm-config">
              <div className="flex items-center gap-3 mb-4">
                <Server className="w-4 h-4 text-[#4a4a60]" />
                <p className="text-xs text-[#e8e8f0]">LLM Configuration</p>
              </div>
              <div className="bg-[#08080c] border border-[#1a1a24] p-3 mb-4">
                <p className="text-[9px] text-[#4a4a60] font-mono uppercase mb-1">Endpoint</p>
                <p className="text-[10px] text-[#7a7a9a] font-mono break-all">rhinoai.app/ollama/v1</p>
              </div>
              <button
                onClick={testLLMConnection}
                disabled={testingLLM}
                className="w-full border border-[#00d4ff] text-[#00d4ff] font-mono text-[10px] uppercase py-2 flex items-center justify-center gap-2 hover:bg-[#00d4ff]/10 disabled:opacity-50"
                data-testid="test-llm-btn"
              >
                {testingLLM && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Test Connection
              </button>
              {llmStatus && (
                <div className={`mt-3 p-3 border ${
                  llmStatus.success 
                    ? 'border-[#00ff88]/30 bg-[#00ff88]/5' 
                    : 'border-[#ff3366]/30 bg-[#ff3366]/5'
                }`}>
                  {llmStatus.success ? (
                    <div className="flex items-center gap-2 text-[#00ff88] text-xs">
                      <Check className="w-3.5 h-3.5" />
                      Connected ({llmStatus.latency}ms)
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-[#ff3366] text-xs">
                      <AlertTriangle className="w-3.5 h-3.5" />
                      {llmStatus.error}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="bg-[#0d0d12] border border-[#1a1a24] p-4">
              <p className="text-[9px] text-[#4a4a60] font-mono uppercase mb-1">App Version</p>
              <p className="text-xs text-[#e8e8f0]">Sage App v1.0.0</p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
